/**
 * ReportFooter — bottom-of-report footer block driven by theme.
 *
 * Up to this commit the in-product report had no surface that read
 * `theme.footer*` — the fields existed on `ReportTheme` but were
 * rendered nowhere. The DOCX export's footer paragraph still drives
 * the matching block in exported `.docx` files; this component
 * keeps the on-screen / PDF render in sync.
 *
 * Defaults: when `theme.footerText` is empty (or whitespace),
 * fall back to the universal "Confidential – For Authorized Use
 * Only" line so a tenant with a partially-configured theme still
 * gets a sane footer instead of a blank line.
 *
 * Layout: one centred line — `[primary] · [subtext] · [email]` —
 * mirroring the "End of report · Generated … · N sources cited"
 * stamp directly above it. The left margin / vertical padding now
 * comes from the `data-page="final"` wrapper in SiteRagReportView,
 * so this component carries no positioning of its own.
 */
import React from 'react';
import type { ReportTheme } from '../../types/reportTheme';
import type { DemonstrationDisclosureV1 } from '../../types/demonstration';

export interface ReportFooterProps {
  theme: Pick<ReportTheme, 'footerText' | 'footerSubtext' | 'footerEmail'>;
  demonstrationDisclosure?: DemonstrationDisclosureV1;
}

const DEFAULT_FOOTER_TEXT = 'Confidential – For Authorized Use Only';

function isMeaningful(s: string | undefined): boolean {
  return Boolean(s && s.trim().length > 0);
}

export const ReportFooter: React.FC<ReportFooterProps> = ({ theme, demonstrationDisclosure }) => {
  const primary = isMeaningful(theme.footerText) ? theme.footerText : DEFAULT_FOOTER_TEXT;
  const subtext = isMeaningful(theme.footerSubtext) ? theme.footerSubtext : undefined;
  const email = isMeaningful(theme.footerEmail) ? theme.footerEmail : undefined;

  const pieces: React.ReactNode[] = [
    <span key="text" data-testid="report-footer-text">{primary}</span>,
  ];
  if (subtext) {
    pieces.push(<span key="subtext" data-testid="report-footer-subtext">{subtext}</span>);
  }
  if (email) {
    pieces.push(
      <a
        key="email"
        href={`mailto:${email}`}
        className="text-text-secondary hover:text-primary underline-offset-2 hover:underline"
        data-testid="report-footer-email"
      >
        {email}
      </a>,
    );
  }
  if (demonstrationDisclosure) {
    pieces.push(
      <span key="demonstration" data-testid="report-footer-demonstration">
        Demonstration scenario ({demonstrationDisclosure.scenarioId}) — {demonstrationDisclosure.notice}
      </span>,
    );
  }

  return (
    <footer
      id="footer"
      className="text-center text-xs text-text-secondary mt-1"
      data-section="footer"
      data-testid="report-footer"
    >
      {pieces.map((piece, i) => (
        <React.Fragment key={i}>
          {i > 0 ? (
            <span aria-hidden="true" className="mx-2 opacity-60">·</span>
          ) : null}
          {piece}
        </React.Fragment>
      ))}
    </footer>
  );
};
